// Mapping of domain states to the visual tones of the console.
//
// A tone is a colour AND a shape (see StatusChip), so the mapping is the single
// place that decides how loud a state looks. Keep it boring: red only for things
// an operator must act on.

import type { Phase, RouteState, Severity, SiteRole } from '@/api/types'
import type { MessageKey } from '@/shared/i18n'
import { sharedUi } from '@/shared/i18n/messages'

type UiKey = MessageKey<typeof sharedUi.ru>

export type Tone = 'ok' | 'warn' | 'alarm' | 'air' | 'info' | 'idle'

export const TONES: readonly Tone[] = ['ok', 'warn', 'alarm', 'air', 'info', 'idle']

/** How much attention a tone demands; higher wins in worstTone(). */
const TONE_RANK: Record<Tone, number> = {
  idle: 0,
  info: 1,
  ok: 2,
  air: 3,
  warn: 4,
  alarm: 5,
}

/**
 * The tone that should represent a group (a channel tile summarising inputs,
 * branches and legs). An empty group is idle.
 */
export function worstTone(tones: Iterable<Tone>): Tone {
  let worst: Tone = 'idle'
  for (const t of tones) if (TONE_RANK[t] > TONE_RANK[worst]) worst = t
  return worst
}

const PHASE_TONE: Record<Phase, Tone> = {
  idle: 'idle',
  starting: 'info',
  running: 'ok',
  // Works, but not as configured: bypass, optional leg down, ffmpeg speed < 0.95.
  degraded: 'warn',
  // Failed and waiting for the supervisor to restart it: self-healing, so not red yet.
  // If it keeps failing, the crashloop ALARM is what turns the wall red.
  backoff: 'warn',
  // Cannot run until somebody changes the configuration.
  failed: 'alarm',
  // Deliberately not running (disabled channel, route off): quiet, not a fault.
  stopped: 'idle',
  // A URL input whose video played to its end (on_end slate/stop): what the
  // operator asked for, not a fault - but worth seeing.
  ended: 'info',
}

const PHASE_LABEL: Record<Phase, UiKey> = {
  idle: 'phaseIdle',
  starting: 'phaseStarting',
  running: 'phaseRunning',
  degraded: 'phaseDegraded',
  backoff: 'phaseBackoff',
  failed: 'phaseFailed',
  stopped: 'phaseStopped',
  ended: 'phaseEnded',
}

export function phaseTone(phase: Phase): Tone {
  return PHASE_TONE[phase] ?? 'idle'
}

export function phaseLabel(phase: Phase): string {
  const key = PHASE_LABEL[phase]
  return key ? sharedUi.t(key) : phase
}

const SEVERITY_TONE: Record<Severity, Tone> = {
  info: 'info',
  warning: 'warn',
  critical: 'alarm',
}

const SEVERITY_LABEL: Record<Severity, UiKey> = {
  info: 'severityInfo',
  warning: 'severityWarning',
  critical: 'severityCritical',
}

export function severityTone(severity: Severity): Tone {
  return SEVERITY_TONE[severity] ?? 'info'
}

export function severityLabel(severity: Severity): string {
  const key = SEVERITY_LABEL[severity]
  return key ? sharedUi.t(key) : severity
}

const ROUTE_TONE: Record<RouteState, Tone> = {
  off: 'idle',
  armed: 'info',
  on_air: 'air',
}

const ROUTE_LABEL: Record<RouteState, UiKey> = {
  off: 'routeOff',
  armed: 'routeArmed',
  on_air: 'routeOnAir',
}

export function routeStateTone(state: RouteState): Tone {
  return ROUTE_TONE[state] ?? 'idle'
}

export function routeStateLabel(state: RouteState): string {
  const key = ROUTE_LABEL[state]
  return key ? sharedUi.t(key) : state
}

const ROUTE_PLAIN_LABEL: Record<RouteState, UiKey> = {
  off: 'routePlainOff',
  armed: 'routePlainArmed',
  on_air: 'routeOnAir',
}

/**
 * The basic-view words of the dictionary (ux-simplify §2): "Подключено, не в
 * эфире" instead of the engineer's "Готова". `null` = nothing is connected to
 * the destination at all ("Ничего не подключено"). routeStateLabel keeps the
 * advanced-view words (Подачи, Линии).
 */
export function routeStatePlainLabel(state: RouteState | null): string {
  if (state === null) return sharedUi.t('routePlainNothing')
  const key = ROUTE_PLAIN_LABEL[state]
  return key ? sharedUi.t(key) : state
}

const SITE_ROLE_LABEL: Record<SiteRole, UiKey> = {
  active: 'siteActive',
  standby: 'siteStandby',
  capture: 'siteCapture',
}

export function siteRoleLabel(role: SiteRole): string {
  const key = SITE_ROLE_LABEL[role]
  return key ? sharedUi.t(key) : role
}

/** An offline agent is an alarm only for a site that is supposed to carry air. */
export function agentTone(online: boolean, role: SiteRole): Tone {
  if (online) return 'ok'
  return role === 'standby' ? 'warn' : 'alarm'
}
